import React, { useState } from 'react';
import { 
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from '../components/ui/accordion';
import { MessageSquare, Clock, Target, DollarSign, CheckCircle } from 'lucide-react';
import ContactModal from '../components/ContactModal';
import { trackButtonClick } from '../utils/analytics';
import mockData from '../mock';

const FAQ = () => {
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [modalTitle, setModalTitle] = useState('Get Your Free Strategy');

  // Icon mapping for FAQ items
  const getIcon = (id) => {
    switch (id) {
      case 'start':
        return MessageSquare;
      case 'guarantees':
        return CheckCircle;
      case 'niche':
        return Target;
      case 'timeline':
        return Clock;
      case 'budget':
        return DollarSign;
      default:
        return MessageSquare;
    }
  };

  const handleModalOpen = (title, buttonType) => {
    trackButtonClick(title, 'faq_page');
    setModalTitle(title);
    setIsModalOpen(true);
  };

  return (
    <div className="min-h-screen pt-20" style={{ backgroundColor: 'var(--bg-page)' }}>
      {/* Header Section */}
      <section className="py-20">
        <div className="container max-w-6xl mx-auto px-6">
          <div className="text-center mb-16">
            <h1 className="heading-1 mb-6 fade-in" style={{ color: 'var(--text-primary)' }}>Frequently Asked Questions</h1>
            <p className="body-large max-w-3xl mx-auto fade-in fade-in-delay-1" style={{ color: 'var(--text-secondary)' }}>
              Get answers to the most common questions about our advertising services, 
              process, and how we can help grow your business.
            </p>
          </div>
        </div>
      </section>

      {/* FAQ Section */}
      <section className="py-20">
        <div className="container max-w-6xl mx-auto px-6">
          <div className="max-w-4xl mx-auto">
            <div 
              className="service-card fade-in fade-in-delay-2 p-8 rounded-2xl border"
              style={{ 
                backgroundColor: 'var(--bg-card)',
                borderColor: 'var(--border-medium)'
              }}
            >
              <Accordion type="single" collapsible className="w-full">
                {mockData.faq.map((item, index) => {
                  const IconComponent = getIcon(item.id);
                  
                  return (
                    <AccordionItem 
                      key={item.id} 
                      value={item.id}
                      className="border-b last:border-b-0 py-2"
                      style={{ borderColor: 'var(--border-medium)' }}
                    >
                      <AccordionTrigger className="text-left py-6 hover:no-underline group">
                        <div className="flex items-center">
                          <div className="p-2 rounded-full mr-4 group-hover:bg-opacity-30 transition-colors" style={{ backgroundColor: 'rgba(217, 251, 6, 0.1)' }}>
                            <IconComponent className="h-4 w-4" style={{ color: 'var(--brand-primary)' }} />
                          </div>
                          <span className="heading-4 group-hover:text-brand-primary transition-colors" style={{ color: 'var(--text-primary)' }}>
                            {item.question}
                          </span>
                        </div>
                      </AccordionTrigger>
                      
                      <AccordionContent className="pb-6 pl-12">
                        <p className="body-medium leading-relaxed" style={{ color: 'var(--text-secondary)' }}>
                          {item.answer}
                        </p>
                      </AccordionContent>
                    </AccordionItem>
                  );
                })}
              </Accordion>
            </div>
          </div>
        </div>
      </section>

      {/* Additional Info Section */}
      <section className="py-20" style={{ backgroundColor: 'var(--bg-card)' }}>
        <div className="container max-w-6xl mx-auto px-6">
          <div className="text-center mb-16">
            <h2 className="heading-2 mb-6 fade-in" style={{ color: 'var(--text-primary)' }}>Still Have Questions?</h2>
            <p className="body-large max-w-2xl mx-auto fade-in fade-in-delay-1" style={{ color: 'var(--text-secondary)' }}>
              We're here to help. Contact us for a free consultation and get personalized answers to your specific needs.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {[
              {
                title: 'Free Consultation',
                description: 'Schedule a 30-minute call to discuss your advertising goals and challenges.',
                action: 'Book Now'
              },
              {
                title: 'Custom Strategy',
                description: 'Get a personalized media plan tailored to your business and budget.',
                action: 'Get Strategy'
              },
              {
                title: 'Quick Response',
                description: 'We typically respond to all inquiries within 2 hours during business days.',
                action: 'Contact Us'
              }
            ].map((item, index) => (
              <div 
                key={index} 
                className={`service-card text-center fade-in fade-in-delay-${index + 1} p-8 rounded-2xl border transition-transform hover:scale-105`}
                style={{ 
                  backgroundColor: 'var(--bg-page)',
                  borderColor: 'var(--border-medium)'
                }}
              >
                <h3 className="heading-4 mb-4" style={{ color: 'var(--text-primary)' }}>{item.title}</h3>
                <p className="body-medium mb-6" style={{ color: 'var(--text-secondary)' }}>
                  {item.description}
                </p>
                <button 
                  className="btn-secondary px-6 py-3 rounded-full font-semibold border transition-all duration-300 hover:scale-105"
                  style={{ 
                    backgroundColor: 'transparent',
                    color: 'var(--brand-primary)',
                    borderColor: 'var(--brand-primary)'
                  }}
                  onClick={() => handleModalOpen(item.action, item.action.toLowerCase().replace(' ', '_'))}
                >
                  {item.action}
                </button>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Contact CTA */}
      <section className="py-20">
        <div className="container max-w-6xl mx-auto px-6">
          <div className="text-center">
            <div 
              className="service-card max-w-4xl mx-auto fade-in p-16 rounded-2xl border"
              style={{ 
                backgroundColor: 'var(--bg-card)',
                borderColor: 'var(--border-medium)'
              }}
            >
              <h2 className="heading-2 mb-6" style={{ color: 'var(--text-primary)' }}>Ready to Start Your Campaign?</h2>
              <p className="body-large mb-8 max-w-2xl mx-auto" style={{ color: 'var(--text-secondary)' }}>
                Don't let another day pass without maximizing your advertising potential. 
                Let's create a strategy that brings real results to your business.
              </p>
              <button 
                className="btn-primary px-8 py-4 rounded-full font-semibold text-lg transition-all duration-300 hover:scale-105"
                style={{ 
                  backgroundColor: 'var(--brand-primary)',
                  color: 'var(--text-inverse)'
                }}
                onClick={() => handleModalOpen('Get Your Free Strategy', 'get_strategy')}
              >
                Get Your Free Strategy
              </button>
            </div>
          </div>
        </div>
      </section>

      {/* Contact Modal */}
      <ContactModal 
        isOpen={isModalOpen} 
        onClose={() => setIsModalOpen(false)}
        title={modalTitle}
      />
    </div>
  );
};

export default FAQ;